import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useMoney } from "../../../../../currency/useMoney"
import { CustomerGroupSalesByAccountResponse } from "../../accountingApi"
import { Box } from "@mui/system"
import { Skeleton, Table, TableBody, TableCell, TableFooter, TableHead, TableRow, Typography } from "@mui/material"
import { isEmpty } from "lodash"
import { ExpandableTableRow } from "../ExpandableTableRow"
import { toDinero } from "../../../../../currency/currencyLib"
import { UseQueryResult } from "@tanstack/react-query"
import { TableSummaryWrap, TableTotalAmountBox } from "../../../../../../shared/components/table/TableSummaryBox"
import { makeStyles } from "tss-react/mui"

type CustomerGroupAccordionDetailsDetailsProps = Readonly<{
    groupSalesByAccounts: UseQueryResult<CustomerGroupSalesByAccountResponse, unknown>
}>

const useStyles = makeStyles()(() => ({
    table: {
        borderRadius: ".75rem",
        overflow: "hidden",
        boxShadow: "0px 2px 6px 0px #00000014",
    },
    subRowProduct: {
        marginLeft: "3.5rem",
    },
}))

export function CustomerGroupAccordionDetails({ groupSalesByAccounts }: CustomerGroupAccordionDetailsDetailsProps) {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const moneyFactory = useMoney({})

    if (groupSalesByAccounts.isLoading) {
        return (
            <Box sx={{ width: "100%" }}>
                <Skeleton />
                <Skeleton />
                <Skeleton />
            </Box>
        )
    }

    if (isEmpty(groupSalesByAccounts.data?.salesByAccount.categories)) {
        return <></>
    }

    return (
        <>
            {groupSalesByAccounts.data?.salesByAccount.categories.map((category) => {
                return (
                    <>
                        <Typography
                            variant="body2"
                            gutterBottom
                            marginBottom={"1rem"}
                            marginTop={"2rem"}
                            sx={{ fontWeight: "bold" }}
                        >
                            {category.name}
                        </Typography>
                        <Table className={classes.table}>
                            <TableHead sx={{ backgroundColor: "#F8FAFC" }}>
                                <TableRow>
                                    <TableCell></TableCell>
                                    <TableCell sx={{ textAlign: "end" }}>
                                        {category.accounts.some((acc) => {
                                            return acc.products && acc.products.length > 0
                                        }) && l10n.getString("accounting-product-sale-product-quantity")}
                                    </TableCell>
                                    <TableCell sx={{ textAlign: "end" }}>
                                        {l10n.getString("accounting-amount")}
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {category.accounts.map((customerAccount) => {
                                    return (
                                        <>
                                            {customerAccount.products ? (
                                                <ExpandableTableRow
                                                    cellValue={customerAccount.name}
                                                    cellValue2={moneyFactory.format(toDinero(customerAccount.amount))}
                                                >
                                                    {customerAccount.products &&
                                                        customerAccount.products.map((product) => {
                                                            return (
                                                                <TableRow>
                                                                    <TableCell>
                                                                        <Typography
                                                                            variant="body2"
                                                                            sx={{
                                                                                marginLeft: "2.4rem",
                                                                            }}
                                                                        >
                                                                            {product.name}
                                                                        </Typography>
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: "end",
                                                                        }}
                                                                    >
                                                                        {product.numberOfItems}
                                                                    </TableCell>
                                                                    <TableCell
                                                                        sx={{
                                                                            textAlign: "end",
                                                                        }}
                                                                    >
                                                                        {moneyFactory.format(
                                                                            toDinero(product.totalAmount),
                                                                        )}
                                                                    </TableCell>
                                                                </TableRow>
                                                            )
                                                        })}
                                                </ExpandableTableRow>
                                            ) : (
                                                <TableRow>
                                                    <TableCell>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                marginLeft: "2.4rem",
                                                            }}
                                                        >
                                                            {customerAccount.name}
                                                        </Typography>
                                                    </TableCell>
                                                    <TableCell sx={{ textAlign: "end" }}></TableCell>
                                                    <TableCell sx={{ textAlign: "end" }}>
                                                        {moneyFactory.format(toDinero(customerAccount.amount))}
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </>
                                    )
                                })}
                            </TableBody>
                            <TableFooter>
                                <TableRow>
                                    <TableCell colSpan={3} sx={{ padding: "0" }}>
                                        <TableSummaryWrap padding="sm">
                                            <TableTotalAmountBox
                                                variant="embedded"
                                                data={category?.accounts}
                                                accessor={(row) => row.amount}
                                            />
                                        </TableSummaryWrap>
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                    </>
                )
            })}
        </>
    )
}
